import * as React from 'react';
import {
	Header,
	Hotspot,
	Icon,
	Tabs,
	TextFilter,
	Box,
	SpaceBetween, Popover,
	Spinner,
} from '@amzn/awsui-components-console';
import { Store } from 'src/stores/store.interface';
import { useStore } from 'src/stores/store';
import { TaskTypeListItem } from 'src/stores/taskTypeListStore/taskTypeListStore.interface';
import ErrorLoader from '../errorLoader/errorLoader';
import './taskTypeList.scss';
import { ReactFlowNodeType } from 'src/components/createWorklow/customNode/customNodeEnums';

const TAB_TASKS = 'tasks';
const TAB_SUB_WORKFLOWS = 'subWorkflows';

const matchesFilter = (item: TaskTypeListItem, filteringText: string) => {
	const needle = filteringText.toLowerCase();

	return (
		item.taskName.toLowerCase().includes(needle) ||
		item.description?.toLowerCase().includes(needle) ||
		item.applicationName?.toLowerCase().includes(needle)
	);
};

export default function TaskTypeList() {
	const isTaskTypeListLoading = useStore(
		(state: Store) => state.isTaskTypeListLoading
	);
	const taskTypeListError = useStore((state: Store) => state.taskTypeListError);
	const taskTypeList: TaskTypeListItem[] = useStore(
		(state: Store) => state.taskTypeList
	);
	const subWorkflowList: TaskTypeListItem[] = useStore(
		(state: Store) => state.subWorkflowList
	);

	const [filteringText, setFilteringText] = React.useState('');
	const [visibleDescription, setVisibleDescription] = React.useState('');
	const [activeTabId, setActiveTabId] = React.useState(TAB_TASKS);

	const onDragStart = (event, node) => {
		const stringNode = JSON.stringify(node);

		event.dataTransfer.setData('application/reactflow', stringNode);
		event.dataTransfer.effectAllowed = 'move';
	};

	const filteredTaskTypesList = React.useMemo(() => {
		return taskTypeList?.filter((taskType) =>
			matchesFilter(taskType, filteringText)
		);
	}, [taskTypeList, filteringText]);

	const filteredSubWorkflowList = React.useMemo(() => {
		return subWorkflowList?.filter((subWorkflow) =>
			matchesFilter(subWorkflow, filteringText)
		);
	}, [subWorkflowList, filteringText]);

	const activeList =
		activeTabId === TAB_SUB_WORKFLOWS
			? filteredSubWorkflowList
			: filteredTaskTypesList;

	return (
		<Box className="taskTypeCntnr">
			<SpaceBetween size="s">
				<Header
					variant="h3"
					description={
						<Box fontSize="body-s" color="text-body-secondary">
							<em>Drag tasks to canvas to build workflow</em>
						</Box>
					}
				>
					<b className='taskTypeHeader'>Supported Tasks, Operations and SubWorkflows</b>
					<Hotspot side="right" hotspotId="draggable-tasks-list"/>
				</Header>
				<Tabs
					activeTabId={activeTabId}
					onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
					tabs={[
						{
							id: TAB_TASKS,
							label: `Tasks (${filteredTaskTypesList?.length ?? 0})`,
						},
						{
							id: TAB_SUB_WORKFLOWS,
							label: `Sub-workflows (${filteredSubWorkflowList?.length ?? 0})`,
						},
					]}
				/>
				<TextFilter
					filteringText={filteringText}
					filteringPlaceholder="Find Supported Task Types"
					filteringAriaLabel="Filter Supported Task Types"
					onChange={({ detail }) => {
						setFilteringText(detail.filteringText);
					}}
					className="mw100"
				/>
			</SpaceBetween>
			{activeList && activeList.length > 0 && (
				<ul className="taskTypeList">
					{activeList.map(
						(
							{
								taskName,
								taskId,
								taskInputs,
								taskOutputs,
								averageExecutionTime,
								owner,
								entityTypeSupported,
								description,
								applicationName,
								operation,
								operationId,
								isSubWorkflow,
								childWorkflowId,
							},
							index
						) => (
							<li
								key={taskId}
								data-testid={`taskTypeListItem${index}`}
								className="reactFlowNode input"
								onDragStart={(event) =>
									onDragStart(event, {
										type: ReactFlowNodeType.BI_DIRECTIONAL,
										taskTypeName: taskName,
										taskInputs,
										taskOutputs,
										taskName,
										averageExecutionTime,
										owner,
										entityTypeSupported,
										description,
										applicationName,
										operation,
										operationId,
										isSubWorkflow,
										childWorkflowId,
									})
								}
								draggable
							>
								<Box>
									<Box fontWeight="bold" className="reactFlowNodeName">
										<Icon name="drag-indicator" />
										<span className="taskName">{taskName}</span>
										<Popover
											dismissButton={true}
											position="right"
											size="large"
											triggerType="custom"
											renderWithPortal={true}
											content={
												<Box className="popoverContent">
													<Box padding="s">
														<SpaceBetween size="xs">
															<Box fontWeight="bold">{taskName}</Box>
															<Box>{description}</Box>
														</SpaceBetween>
													</Box>
												</Box>
											}
										>
											<Icon name="status-info" variant="link" />
										</Popover>
									</Box>
									<Box fontSize="body-s" color="text-body-secondary" className="taskApplicationName">{applicationName ?? 'SNOWPlatform'}</Box>
								</Box>
								{visibleDescription === description && (
									<Box padding={{ left: "xl" }} margin={{ top: "s" }}>
										{description}
									</Box>
								)}
							</li>
						)
					)}
				</ul>
			)}
			{isTaskTypeListLoading ? (
				<Box textAlign="center" padding="s">
					<Spinner size="normal" />
					<Box color="text-status-inactive" margin={{ left: 's' }}>
						Loading supported tasks...
					</Box>
				</Box>
			) : (
				<ErrorLoader
					isLoading={false}
					error={taskTypeListError}
				/>
			)}
		</Box>
	);
}
